import { useState } from 'react';
import { AlertCircleIcon, CheckCircle2Icon, FileTextIcon, Loader2Icon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { observationLetterUrl } from '../../api/research';

interface CoverLetterUploadProps {
  error?: string;
}

/**
 * The cover letter ResConsentForm.aspx asks for when the SRC or the IRB has
 * sent the proposal back. It answers the committee's observations and is
 * merged in front of the proposal when it is resubmitted.
 */
export function CoverLetterUpload({ error }: CoverLetterUploadProps) {
  const { config, data, status, sessionToken, uploadCoverLetter } = usePortal();
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState('');

  const maxMb = config?.maxDocumentMB ?? 1;
  const locked = Boolean(status && !status.canEdit);
  const letter = data.coverLetter;
  const from = letter.statusId === '07' ? 'the IRB' : 'the Scientific Research Committee';

  const handleFile = async (file: File | undefined, input: HTMLInputElement) => {
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setFileError('The cover letter should be a pdf file.');
      input.value = '';
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      setFileError(`The cover letter should be a pdf file up to ${maxMb} MB of size.`);
      input.value = '';
      return;
    }

    setFileError('');
    setBusy(true);
    const failure = await uploadCoverLetter(file);
    setBusy(false);
    input.value = '';
    if (failure) setFileError(failure);
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Upload the Cover Letter</h2>
          <p className="card-subtitle">
            Your proposal was sent back by {from}. Reply to their observations in a cover
            letter — it should be a pdf file up to {maxMb} MB of size.
          </p>
        </div>
      </div>
      <div className="card-body">
        {letter.hasObservationLetter && sessionToken && status ? (
          <p style={{ marginBottom: 'var(--space-4)' }}>
            <a
              className="file-name"
              href={observationLetterUrl(sessionToken, status.researchCode)}
              target="_blank"
              rel="noreferrer"
            >
              <FileTextIcon size={14} aria-hidden="true" />
              Read the committee's observation letter
            </a>
          </p>
        ) : null}

        <label htmlFor="cover-letter" className="field-label">
          Cover Letter
          <span className="req" aria-hidden="true">
            *
          </span>
        </label>

        {letter.uploaded ? (
          <p className="muted inline-actions" style={{ alignItems: 'center' }}>
            <CheckCircle2Icon size={14} aria-hidden="true" />
            {letter.fileName || 'Cover letter'} uploaded. Choose another file to replace it.
          </p>
        ) : null}

        {busy ? (
          <span className="muted inline-actions" style={{ alignItems: 'center' }}>
            <Loader2Icon size={14} className="spin" aria-hidden="true" />
            Uploading…
          </span>
        ) : (
          <input
            id="cover-letter"
            className="file-input"
            type="file"
            accept="application/pdf"
            disabled={locked}
            onChange={(e) => void handleFile(e.target.files?.[0], e.currentTarget)}
          />
        )}

        {fileError || error ? (
          <p className="error-text" role="alert">
            <AlertCircleIcon size={13} aria-hidden="true" />
            {fileError || error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
